import { useAppStore } from '@/stores/app.store';
import { computed } from 'vue';
import _ from 'lodash';

export default function useTranslations(): {
  bbCodeToHtml: (input: string) => string;
  htmlToBBCode: (html: string) => string;
  translate: (key: string, params?: string[]) => string;
  prepareHtmlToDisplay: (bbCode: string) => string;
  sanitizeCode: (bbCode: string) => string;
  decodeEntities: (text: string) => string;
} {
  const appStore = useAppStore();
  const translations = computed(() => {
    return appStore?.translations;
  });

  function translate(key: string, params: string[] = []): string {
    let translateString = translations?.value[key] ?? 'missing translation: ' + key;
    if (params.length > 0) translateString = replacePlaceholders(translateString, params);
    return translateString;
  }

  function replacePlaceholders(text: string, params: string[]): string {
    _.each(params, function (val, key) {
      const regexp = new RegExp('\\{' + key + '\\}', 'g');
      text = text.replace(regexp, val);
    });

    return text;
  }

  function removeBBCode(input: string): string {
    return input.replace(/\[\/?[a-zA-Z0-9#=]+\]/g, '');
  }
  function removeHtml(input: string): string {
    return input.replace(/(<([^>]+)>)/gi, '');
  }

  function sanitizeCode(input: string): string {
    return removeBBCode(removeHtml(input));
  }

  function bbCodeToHtml(input: string): string {
    input = input.replace(/&lt;/g, '<');
    input = input.replace(/&gt;/g, '>');

    return input;
  }

  const decodeEntities = (text: string): string => {
    if (!text) return '';

    text = text.replace(/&nbsp;/gi, ' ').replace(/\u00a0/g, ' ');

    const ta = document.createElement('textarea');
    ta.innerHTML = text;
    return ta.value;
  };

  function htmlToBBCode(html: string): string {
    if (!html) return '';

    html = html.replace(/<pre(.*?)>(.*?)<\/pre>/gim, '[code]$2[/code]');
    html = html.replace(/<h[1-7](.*?)>(.*?)<\/h[1-7]>/gim, '\n[h]$2[/h]\n'); // add /g
    html = html.replace(/<ul(.*?)>/gi, '[list]');
    html = html.replace(/<\/ul>/gi, '[/list]');
    html = html.replace(/<li(.*?)><p>(.*?)<\/p><\/li>/gi, '[*]$2');
    html = html.replace(/<li(.*?)>(.*?)<\/li>/gi, '[*]$2');
    html = html.replace(/<span style="font-style: italic;">(.*?)<\/span>/gim, '[i]$1[/i]');
    html = html.replace(/<span style="text-decoration: underline;">(.*?)<\/span>/gim, '[u]$1[/u]');
    html = html.replace(/<div class="text-center">(.*?)<\/div>/gim, '[center]$1[/center]');
    html = html.replace(/<div style="text-align: left;">(.*?)<\/div>/gim, '[left]$1[/left]');
    html = html.replace(/<div style="text-align: right;">(.*?)<\/div>/gim, '[right]$1[/right]');
    html = html.replace(/<p style="text-align: center;">(.*?)<\/p>/gim, '[center]$1[/center]');
    html = html.replace(/<p style="text-align: left;">(.*?)<\/p>/gim, '[left]$1[/left]');
    html = html.replace(/<p style="text-align: right;">(.*?)<\/p>/gim, '[right]$1[/right]');
    html = html.replace(/<p[^>]*>/gi, '[p]');
    html = html.replace(/<\/p>/gi, '[/p]');
    html = html.replace(/<br(.*?)>/gi, '\n');
    html = html.replace(/<textarea(.*?)>(.*?)<\/textarea>/gim, '[code]$2[/code]');
    html = html.replace(/<b>/gi, '[b]');
    html = html.replace(/<i>/gi, '[i]');
    html = html.replace(/<u>/gi, '[u]');
    html = html.replace(/<\/b>/gi, '[/b]');
    html = html.replace(/<\/i>/gi, '[/i]');
    html = html.replace(/<\/u>/gi, '[/u]');
    html = html.replace(/<em>/gi, '[i]');
    html = html.replace(/<\/em>/gi, '[/i]');
    html = html.replace(/<strong>/gi, '[b]');
    html = html.replace(/<\/strong>/gi, '[/b]');
    html = html.replace(/<cite>/gi, '[i]');
    html = html.replace(/<\/cite>/gi, '[/i]');
    html = html.replace(/<font color="(.*?)">(.*?)<\/font>/gim, '[color=$1]$2[/color]');
    html = html.replace(/<font color=(.*?)>(.*?)<\/font>/gim, '[color=$1]$2[/color]');
    html = html.replace(/<link(.*?)>/gi, '');
    html = html.replace(/<div>/gi, '\n');
    html = html.replace(/<\/div>/gi, '\n');
    html = html.replace(/<td(.*?)>/gi, ' ');
    html = html.replace(/<tr(.*?)>/gi, '\n');
    html = html.replace(/<img(.*?)src="(.*?)"(.*?)>/gi, '[img]$2[/img]');
    html = html.replace(/<a(.*?)href="(.*?)"(.*?)>(.*?)<\/a>/gi, '[url=$2]$4[/url]');
    html = html.replace(/<head>(.*?)<\/head>/gim, '');
    html = html.replace(/<object>(.*?)<\/object>/gim, '');
    html = html.replace(/<script(.*?)>(.*?)<\/script>/gim, '');
    html = html.replace(/<style(.*?)>(.*?)<\/style>/gim, '');
    html = html.replace(/<title>(.*?)<\/title>/gim, '');
    html = html.replace(/<!--(.*?)-->/gim, '\n');
    html = html.replace(/\/\//gi, '/');
    html = html.replace(/http:\//gi, 'http://');
    html = html.replace(/https:\//gi, 'https://');
    html = html.replace(/<(?:[^>'"]*|(['"]).*?\1)*>/gim, '');
    html = html.replace(/\r\r/gi, '');
    html = html.replace(/\[h](.*?)\[\/h]/gi, '[h2]$1[/h2]');

    html = decodeEntities(html);

    return html;
  }

  function prepareHtmlToDisplay(htmlCode: string): string {
    htmlCode = htmlCode?.replaceAll('\n', '<br>');

    return htmlCode;
  }

  return {
    translate,
    bbCodeToHtml,
    htmlToBBCode,
    prepareHtmlToDisplay,
    sanitizeCode,
    decodeEntities
  };
}
